import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { AlertCircle, BarChart3, Brain, CheckCircle, ChevronRight, ClipboardList, Eye, FileText, Home, Phone, RefreshCw, Search, Shield, User, UserCheck, UserPlus, Users, XCircle } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalChildAssignment = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [observers, setObservers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedChild, setSelectedChild] = useState(null);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      const [childrenRes, observersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/children`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/observers`, { params: { token } })
      ]);
      
      setChildren(childrenRes.data.children || []);
      setObservers(observersRes.data.observers || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const assignChild = async (childId, observerId) => {
    setAssigning(true);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/assign-child`, null, {
        params: { token, child_id: childId, observer_id: observerId }
      });
      
      setSelectedChild(null);
      await loadData();
      alert('Child assigned successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign child');
    } finally {
      setAssigning(false);
    }
  };

  const unassignChild = async (childId) => {
    if (!confirm('Remove this child from observer?')) return;
    
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/unassign-child`, null, {
        params: { token, child_id: childId }
      });
      await loadData();
    } catch (error) {
      alert('Failed to unassign child');
    }
  };

  const filteredChildren = children.filter(child => {
    const matchesSearch = child.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || 
      (filter === 'assigned' && child.observer) ||
      (filter === 'unassigned' && !child.observer);
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: children.length,
    assigned: children.filter(c => c.observer).length,
    unassigned: children.filter(c => !c.observer).length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <UserPlus className="w-7 h-7 text-sanjaya-navy" />
                Child Assignment
              </h1>
              <p className="text-slate-600">Assign children to observers</p>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total Children</p>
                <p className="text-2xl font-bold text-sanjaya-navy">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Assigned</p>
                <p className="text-2xl font-bold text-green-600">{stats.assigned}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Unassigned</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.unassigned}</p>
              </CardContent>
            </Card>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search children..."
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'assigned', 'unassigned'].map(f => (
                <Button
                  key={f}
                  variant={filter === f ? 'default' : 'outline'}
                  onClick={() => setFilter(f)}
                  size="sm"
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          </div>

          {filteredChildren.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No children found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredChildren.map(child => (
                <Card key={child.id} className={`hover:shadow-md transition-shadow ${!child.observer ? 'border-l-4 border-l-yellow-400' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-sanjaya-navy" />
                        </div>
                        <div>
                          <Link to={`/principal/child/${child.id}`} className="font-semibold text-slate-800 hover:text-sanjaya-navy hover:underline">
                            {child.name}
                          </Link>
                          <p className="text-sm text-slate-500">Age: {child.age || 'N/A'}</p>
                        </div>
                      </div>
                      
                      {child.observer ? (
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Assigned to</p>
                            <p className="font-medium text-green-600">{child.observer.name}</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => unassignChild(child.id)} className="text-red-600">
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button onClick={() => setSelectedChild(child)} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
                          <UserPlus className="w-4 h-4 mr-2" /> Assign
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Assignment Modal */}
          {selectedChild && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="w-full max-w-lg m-4">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4">Assign {selectedChild.name}</h2>
                  <p className="text-slate-600 mb-4">Select an observer:</p>
                  
                  {observers.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No observers available</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {observers.filter(o => o.is_accepting_new_children).map(obs => (
                        <button
                          key={obs.id}
                          onClick={() => assignChild(selectedChild.id, obs.id)}
                          disabled={assigning}
                          className="w-full p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-300 flex items-center justify-between transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              {obs.name?.charAt(0)}
                            </div>
                            <div className="text-left">
                              <p className="font-medium">{obs.name}</p>
                              <p className="text-sm text-slate-500">{obs.assigned_children || 0} children</p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 flex justify-end">
                    <Button variant="outline" onClick={() => setSelectedChild(null)}>Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrincipalChildAssignment;
